/**
 * ShiftCycles — repeating patterns of timetables and rest days.
 *
 * Migrated from mock data to live API as part of Wave 3.2. Fetches
 * both Cycles and Timetables on mount — the timetables catalog
 * drives the per-day chip picker and the list-view "Pattern" cell.
 *
 * Backend stores days as [{ day_number, type, timetable_id }] where
 * day_number is a 0-BASED SLOT INDEX into the repeating pattern —
 * `reindexDays` writes `day_number = i`, and for a week-unit cycle the
 * slot is `week * 7 + weekday` (Mon=0), which both readers look up by
 * identity because the grid may be sparsely filled.
 *
 * This comment used to say "the array index + 1", and that stale
 * sentence is where production's 1..7 rows came from: the calc engine
 * receives a 0-based offset, so a 1-based pattern resolves one slot
 * EARLY and its last slot is never reachable. One live tenant had a
 * Saturday drawn as `off` scored as a work day for ten weeks. Migration
 * 097 renumbers such rows and the API now rejects them on write.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Select,
  MenuItem,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack,
  Cancel,
  DeleteOutlined,
  EditOutlined,
  RefreshOutlined,
  Save,
} from '@mui/icons-material';
import { ApiError, attendanceApi } from '../../../api';
import type {
  ShiftCycle,
  ShiftCycleCreate,
  Timetable,
} from '../../../api/attendance';
import type { Schema } from '../../../api/types';
import { ConfirmDialog, StickyFormActions } from '../../../components/primitives';
import { translateApiError } from '../../../i18n/translateApiError';

type CycleDay = Schema<'CycleDaySchema'>;

const COLOR_OPTIONS = [
  '#2E65D4',
  '#4CAF50',
  '#FF9800',
  '#9C27B0',
  '#F44336',
  '#00BCD4',
  '#795548',
  '#607D8B',
];

const WEEKDAYS_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const PeriodGrid: React.FC<{
  days: { type: string; timetable_id?: string | null; day_number: number }[];
  unit: string;
  timetables: { id: string; name: string }[];
  onToggle: (index: number) => void;
  onPickTimetable: (index: number, ttId: string) => void;
  t: (k: string, o?: Record<string, unknown>) => string;
}> = ({ days, unit, timetables, onToggle, onPickTimetable, t }) => {
  const cols = unit === 'week' ? 7 : Math.min(Math.max(days.length, 1), 7);
  const rows: typeof days[] = [];
  if (unit === 'week') {
    for (let w = 0; w < days.length / 7; w += 1) rows.push(days.slice(w * 7, w * 7 + 7));
  } else {
    for (let r = 0; r < days.length; r += cols) rows.push(days.slice(r, r + cols));
  }
  return (
    <Box>
      {unit === 'week' && (
        <Box sx={{ display: 'grid', gridTemplateColumns: `44px repeat(7, 1fr)`, gap: 0.75, mb: 0.5 }}>
          <Box />
          {WEEKDAYS_KEYS.map((d) => (
            <Typography key={d} variant="caption" sx={{ textAlign: 'center', fontWeight: 700, color: 'text.secondary' }}>
              {t(`attendance:shiftCycles.weekday.${d}`)}
            </Typography>
          ))}
        </Box>
      )}
      <Stack spacing={0.75}>
        {rows.map((row, ri) => (
          <Box key={ri} sx={{ display: 'grid', gridTemplateColumns: unit === 'week' ? `44px repeat(7, 1fr)` : `repeat(${cols}, 1fr)`, gap: 0.75 }}>
            {unit === 'week' && (
              <Typography variant="caption" sx={{ alignSelf: 'center', textAlign: 'center', fontWeight: 700, color: 'text.secondary' }}>
                {t('attendance:shiftCycles.weekN', { n: ri + 1 })}
              </Typography>
            )}
            {row.map((cell) => {
              const idx = cell.day_number;
              const isWork = cell.type === 'work';
              return (
                <Box key={idx} sx={{
                  border: '1px solid', borderColor: isWork ? 'primary.main' : 'divider',
                  bgcolor: isWork ? 'rgba(48,103,209,0.06)' : 'grey.50',
                  borderRadius: 1, p: 0.75, minHeight: 58,
                  display: 'flex', flexDirection: 'column', gap: 0.5,
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {unit !== 'week' && (
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>
                        {t('attendance:shiftCycles.dayN', { n: idx + 1 })}
                      </Typography>
                    )}
                    <Chip
                      label={isWork ? t('attendance:shiftCycles.cell.work') : t('attendance:shiftCycles.cell.off')}
                      size="small" color={isWork ? 'primary' : 'default'} variant={isWork ? 'filled' : 'outlined'}
                      onClick={() => onToggle(idx)}
                      sx={{ height: 18, fontSize: '0.6rem', borderRadius: 0.5, cursor: 'pointer', ml: 'auto' }}
                    />
                  </Box>
                  {isWork && timetables.length > 0 && (
                    <Select
                      size="small" variant="standard" disableUnderline
                      value={cell.timetable_id ?? ''}
                      onChange={(e) => onPickTimetable(idx, e.target.value as string)}
                      sx={{ fontSize: '0.62rem' }}
                    >
                      {timetables.map((tt) => (
                        <MenuItem key={tt.id} value={tt.id} sx={{ fontSize: '0.7rem' }}>{tt.name}</MenuItem>
                      ))}
                    </Select>
                  )}
                </Box>
              );
            })}
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

const EMPTY_FORM: ShiftCycleCreate = {
  name: '',
  unit: 'day',
  length: 2,
  days: [],
  color: '#2E65D4',
};

/**
 * Reindex day_number to a 0-based array position (ADR-029). For week-unit
 * cycles the array is ordered week0:Mon..Sun, week1:Mon..Sun, … so the
 * index equals week*7 + weekday (Mon=0), which the calc engine expects.
 */
function reindexDays(days: CycleDay[]): CycleDay[] {
  return days.map((d, i) => ({ ...d, day_number: i }));
}

/** Number of cells in a cycle pattern. */
function patternLength(unit: string | undefined, length: number | undefined): number {
  const n = Math.max(1, length ?? 1);
  return unit === 'week' ? n * 7 : n;
}

/** (Re)generate the days array to match unit×length, preserving existing
 *  cells by position and defaulting new ones to work(first timetable). */
function ensureGrid(days: CycleDay[], unit: string, length: number, firstTt: string | null): CycleDay[] {
  const total = patternLength(unit, length);
  const out: CycleDay[] = [];
  for (let i = 0; i < total; i += 1) {
    const existing = days[i];
    out.push(existing
      ? { ...existing, day_number: i }
      : { day_number: i, type: 'work', timetable_id: firstTt });
  }
  return out;
}

const ShiftCycles: React.FC = () => {
  const { t } = useTranslation();
  const [cycles, setCycles] = useState<ShiftCycle[]>([]);
  const [timetables, setTimetables] = useState<Timetable[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [form, setForm] = useState<ShiftCycleCreate>({ ...EMPTY_FORM });
  const isEditing = !!selectedId || isCreating;

  const [filterText, setFilterText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pendingDelete, setPendingDelete] = useState<ShiftCycle | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cyclesList, ttList] = await Promise.all([
        attendanceApi.shiftCyclesApi.list(),
        attendanceApi.timetablesApi.list(),
      ]);
      setCycles(cyclesList);
      setTimetables(ttList);
    } catch (e) {
      const msg =
        e instanceof ApiError ? translateApiError(e, t) : t('attendance:shiftCycles.loadFailed');
      setError(msg);
      setCycles([]);
      setTimetables([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const timetableById = useMemo(() => {
    const map = new Map<string, Timetable>();
    for (const t of timetables) map.set(t.id, t);
    return map;
  }, [timetables]);

  const filtered = useMemo(
    () =>
      cycles.filter((c) =>
        c.name.toLowerCase().includes(filterText.toLowerCase()),
      ),
    [cycles, filterText],
  );
  const paginated = useMemo(
    () =>
      filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage],
  );

  const handleEdit = (id: string) => {
    const c = cycles.find((x) => x.id === id);
    if (!c) return;
    setForm({
      name: c.name,
      unit: c.unit ?? 'day',
      length: c.length ?? Math.max(1, (c.days ?? []).length),
      days: ensureGrid((c.days ?? []).map((d) => ({ ...d })), c.unit ?? 'day', c.length ?? Math.max(1, (c.days ?? []).length), timetables[0]?.id ?? null),
      color: c.color,
    });
    setSelectedId(id);
    setIsCreating(false);
  };

  const handleCreate = () => {
    const firstTt = timetables[0]?.id ?? null;
    setForm({
      ...EMPTY_FORM,
      days: ensureGrid([], 'day', 2, firstTt),
    });
    setSelectedId(null);
    setIsCreating(true);
  };

  // unit / length controls — regenerate the grid, preserving cells by position.
  const setUnit = (unit: 'day' | 'week') =>
    setForm((p) => ({ ...p, unit, days: ensureGrid(p.days ?? [], unit, p.length ?? 1, timetables[0]?.id ?? null) }));
  const setLength = (length: number) =>
    setForm((p) => ({ ...p, length, days: ensureGrid(p.days ?? [], p.unit ?? 'day', length, timetables[0]?.id ?? null) }));

  const handleCancel = () => {
    setSelectedId(null);
    setIsCreating(false);
  };

  const handleSave = async () => {
    if (!form.name?.trim() || !form.days || form.days.length === 0) return;
    const payload: ShiftCycleCreate = {
      ...form,
      days: reindexDays(form.days),
    };
    setSaving(true);
    try {
      if (isCreating) {
        await attendanceApi.shiftCyclesApi.create(payload);
        setToast(t('attendance:shiftCycles.toastCreated', { name: form.name }));
      } else if (selectedId) {
        await attendanceApi.shiftCyclesApi.update(selectedId, payload);
        setToast(t('attendance:shiftCycles.toastUpdated', { name: form.name }));
      }
      handleCancel();
      await fetchAll();
    } catch (e) {
      const msg = e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.saveFailed');
      setToast(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await attendanceApi.shiftCyclesApi.delete(pendingDelete.id);
      setToast(t('attendance:shiftCycles.toastDeleted', { name: pendingDelete.name }));
      await fetchAll();
    } catch (e) {
      const msg =
        e instanceof ApiError ? translateApiError(e, t) : t('attendance:shiftCycles.deleteFailedReferenced');
      setToast(msg);
      throw e;
    }
  };

  // Cell ops for the period grid.
  const toggleCellType = (index: number) =>
    setForm((p) => ({
      ...p,
      days: (p.days ?? []).map((d, i) =>
        i === index
          ? { ...d, type: d.type === 'work' ? 'off' : 'work', timetable_id: d.type === 'work' ? null : (timetables[0]?.id ?? null) }
          : d,
      ),
    }));

  const setCellTimetable = (index: number, timetable_id: string) =>
    setForm((p) => ({
      ...p,
      days: (p.days ?? []).map((d, i) =>
        i === index ? { ...d, timetable_id, type: 'work' } : d,
      ),
    }));

  const days = form.days ?? [];
  const workCount = days.filter((d) => d.type === 'work').length;
  const offCount = days.filter((d) => d.type === 'off').length;

  // ----- Edit view -----
  if (isEditing) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          py: '12px',
          width: '100%',
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small" onClick={handleCancel} sx={{ mr: 1 }}>
              <ArrowBack />
            </IconButton>
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, fontSize: '1.125rem', color: 'text.primary' }}
              >
                {isCreating ? t('attendance:shiftCycles.newTitle') : t('attendance:shiftCycles.editTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('attendance:shiftCycles.dialog.description')}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                height: '100%',
              }}
            >
              <Box sx={{ px: 2.5, py: 1.25 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'text.primary' }}
                >
                  {t('attendance:shiftCycles.dialog.basicInfo')}
                </Typography>
              </Box>
              <CardContent sx={{ px: 2.5, pt: 0.75, pb: 2 }}>
                <Stack spacing={1.5}>
                  <TextField
                    label={t('attendance:shiftCycles.nameLabel', { defaultValue: 'Cycle name' })}
                    value={form.name}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, name: e.target.value }))
                    }
                    size="small"
                    fullWidth
                    placeholder={t('attendance:shiftCycles.namePlaceholder', { defaultValue: 'e.g. Two Days On, One Off' })}
                  />
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.75 }}
                    >
                      {t('attendance:shiftCycles.dialog.colorTag')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.75 }}>
                      {COLOR_OPTIONS.map((c) => (
                        <Box
                          key={c}
                          onClick={() => setForm((p) => ({ ...p, color: c }))}
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            bgcolor: c,
                            cursor: 'pointer',
                            border: form.color === c ? '2px solid' : '2px solid transparent',
                            borderColor:
                              form.color === c ? 'text.primary' : 'transparent',
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: 'grey.50',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5 }}
                    >
                      {t('attendance:shiftCycles.dialog.summary')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('attendance:shiftCycles.dialog.summaryStats', { days: days.length, work: workCount, off: offCount })}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                height: '100%',
              }}
            >
              <Box
                sx={{
                  px: 2.5,
                  py: 1.25,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'text.primary' }}
                  >
                    {t('attendance:shiftCycles.dialog.cyclePattern')}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: '0.7rem' }}
                  >
                    {t('attendance:shiftCycles.dialog.addDaysHint')}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Select
                    size="small"
                    value={form.unit ?? 'day'}
                    onChange={(e) => setUnit(e.target.value as 'day' | 'week')}
                    sx={{ minWidth: 110, fontSize: '0.8rem' }}
                  >
                    <MenuItem value="day">{t('attendance:shiftCycles.unit.day')}</MenuItem>
                    <MenuItem value="week">{t('attendance:shiftCycles.unit.week')}</MenuItem>
                  </Select>
                  <TextField
                    size="small"
                    type="number"
                    label={t('attendance:shiftCycles.lengthLabel')}
                    value={form.length ?? 1}
                    onChange={(e) => setLength(Math.max(1, Math.min(12, Number(e.target.value) || 1)))}
                    inputProps={{ min: 1, max: 12 }}
                    sx={{ width: 120 }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Stack>
              </Box>
              <CardContent sx={{ px: 2.5, pt: 0.75, pb: 2 }}>
                {timetables.length === 0 && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {t('attendance:shiftCycles.noTimetablesAlert')}
                  </Alert>
                )}
                <PeriodGrid
                  days={days}
                  unit={form.unit ?? 'day'}
                  timetables={timetables}
                  onToggle={toggleCellType}
                  onPickTimetable={setCellTimetable}
                  t={t}
                />
                {days.length > 0 && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 1.5,
                      bgcolor: 'primary.light',
                      borderRadius: 2,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: 'primary.dark',
                        fontSize: '0.72rem',
                      }}
                    >
                      {t('attendance:shiftCycles.dialog.previewLabel')}{' '}
                      {days
                        .map((d) =>
                          d.type === 'off'
                            ? t('attendance:shiftCycles.dialog.dayOff')
                            : timetableById.get(d.timetable_id ?? '')?.name ?? '?',
                        )
                        .join(' → ')}{' '}
                      → {t('attendance:shiftCycles.dialog.repeats')}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <StickyFormActions
  primary={(
    <Button
    variant="contained"
    size="small"
    startIcon={saving ? <CircularProgress size={14} /> : <Save />}
    onClick={handleSave}
    disabled={!form.name?.trim() || days.length === 0 || saving}
    sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}
    >
    {isCreating ? t('attendance:shiftCycles.dialog.createCycle') : t('attendance:shiftCycles.dialog.saveChanges')}
    </Button>
  )}
  secondary={(
    <Button
    variant="outlined"
    size="small"
    startIcon={<Cancel />}
    onClick={handleCancel}
    disabled={saving}
    sx={{
    borderRadius: 1,
    textTransform: 'none',
    fontSize: '0.875rem',
    color: 'text.secondary',
    borderColor: 'divider',
    }}
    >
    {t('common:actions.cancel')}
    </Button>
  )}
/>

        <Snackbar
          open={!!toast}
          autoHideDuration={4000}
          onClose={() => setToast(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            severity="info"
            onClose={() => setToast(null)}
            sx={{ width: '100%' }}
          >
            {toast}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  // ----- List view -----
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        py: '12px',
        width: '100%',
      }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          mb: 1.5,
          gap: 1.5,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, fontSize: '1.125rem', color: 'text.primary' }}
          >
            {t('attendance:shiftCycles.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('attendance:shiftCycles.subtitleDetail')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Tooltip title={t('common:actions.refresh')}>
            <IconButton
              size="small"
              onClick={fetchAll}
              disabled={loading}
              sx={{
                border: '1px solid rgba(0,0,0,0.2)',
                borderRadius: 1,
                bgcolor: '#ffffff',
                '&:hover': { bgcolor: '#f5f5f5' },
              }}
            >
              <RefreshOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}
          >
            {t('attendance:shiftCycles.addButton')}
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <TextField
          variant="outlined"
          label={t('attendance:shiftCycles.filterLabel')}
          value={filterText}
          onChange={(e) => {
            setFilterText(e.target.value);
            setPage(0);
          }}
          size="small"
          sx={{ width: 300 }}
        />
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filtered.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{
            '.MuiTablePagination-toolbar': { pl: 2 },
            '.MuiTablePagination-displayedRows': { m: 0 },
          }}
        />
      </Box>

      {loading ? (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            py: 6,
          }}
        >
          <CircularProgress size={28} />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}
        >
          <Table>
            <TableHead
              sx={{
                bgcolor: 'grey.100',
                '& .MuiTableCell-root': { fontWeight: 600 },
              }}
            >
              <TableRow>
                <TableCell>{t('attendance:shiftCycles.colName', { defaultValue: 'Cycle name' })}</TableCell>
                <TableCell>{t('attendance:shiftCycles.colPattern', { defaultValue: 'Pattern' })}</TableCell>
                <TableCell align="center">{t('attendance:shiftCycles.colDays', { defaultValue: 'Days' })}</TableCell>
                <TableCell align="center">{t('attendance:shiftCycles.colActions', { defaultValue: 'Actions' })}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.map((c) => {
                const cDays = c.days ?? [];
                const work = cDays.filter((d) => d.type === 'work').length;
                const off = cDays.filter((d) => d.type === 'off').length;
                return (
                  <TableRow
                    key={c.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleEdit(c.id)}
                  >
                    <TableCell>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: c.color,
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: 'primary.main',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          {c.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {cDays.map((d, i) => (
                          <Chip
                            key={i}
                            size="small"
                            label={
                              d.type === 'off'
                                ? 'Off'
                                : timetableById
                                    .get(d.timetable_id ?? '')
                                    ?.name?.split(' ')[0] ?? '?'
                            }
                            variant={d.type === 'off' ? 'outlined' : 'filled'}
                            color={d.type === 'off' ? 'default' : 'primary'}
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              borderRadius: 1,
                            }}
                          />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        {t('attendance:shiftCycles.dialog.workOffShort', { work, off })}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="center"
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(c.id);
                          }}
                        >
                          <EditOutlined sx={{ fontSize: 16 }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPendingDelete(c);
                          }}
                          sx={{ '&:hover': { color: 'error.main' } }}
                        >
                          <DeleteOutlined sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
              {paginated.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {filterText
                        ? t('attendance:shiftCycles.noMatch')
                        : t('attendance:shiftCycles.noDefined')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        tone="danger"
        title={t('attendance:shiftCycles.deleteDialog.title')}
        description={t('attendance:shiftCycles.deleteDialog.description')}
        subject={
          pendingDelete && (
            <Box sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
              {pendingDelete.name}
            </Box>
          )
        }
        confirmLabel={t('common:actions.delete')}
        confirmLoadingLabel={t('attendance:shiftCycles.deleteDialog.deleting')}
        onConfirm={handleConfirmDelete}
        onClose={() => setPendingDelete(null)}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          onClose={() => setToast(null)}
          sx={{ width: '100%' }}
        >
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ShiftCycles;
